import { type DayWindowChoice, FilterValueType, type FilterableColumn } from 'app/filter.utils'

/**
 * A band list's window as a Filters rule ("Presented is 0 to 30 days"), stored in `?band=`.
 *
 * The param stays the record because One at a time, the sidebar and old links all read it.
 * `defaultBand` is what the page opens on while the URL names none: the builder draws it as a
 * row so that narrowing is never invisible, and removing the row writes `band=`, every band.
 * `boolean` is the builder's one-choice picker, with its operator locked to "is".
 */
export function bandRuleColumn({
  name,
  group,
  bands,
  dayWindows,
  defaultBand,
}: {
  name: string
  group?: string
  dayWindows?: ReadonlyArray<DayWindowChoice>
  bands: ReadonlyArray<{ id: string; label: string }>
  defaultBand?: string
}): FilterableColumn {
  return {
    attribute: `band`,
    displayName: name,
    group,
    type: FilterValueType.boolean,
    uniqOperator: `exactly`,
    defaultOperator: `exactly`,
    customSearchParam: `band`,
    options: bands.map((band) => ({ id: band.id, name: band.label, value: band.id })),
    dayWindows,
    ...(defaultBand && { paramDefault: true, defaultValue: defaultBand }),
  }
}

/**
 * The Locations button beside Filters on a board. Declared as a builder column, `standalone`, so
 * applying the builder keeps the location in the `filters` param it shares.
 */
export const LOCATION_RULE_COLUMN: Readonly<FilterableColumn> = {
  attribute: `locationId`,
  displayName: `Location`,
  optionsSource: `/api/locations`,
  contextualFiltering: { workspaceId: `workspaceId` },
  uniqOperator: `in`,
  type: FilterValueType.id,
  group: `General`,
  standalone: true,
}

/**
 * What a treatment patient's next visit is for ("Next visit includes Broken, not rebooked"),
 * stored in `?visit=`. Read by `hiddenFromRules`.
 */
export const NEXT_VISIT_RULE_COLUMN: Readonly<FilterableColumn> = {
  attribute: `nextVisit`,
  displayName: `Next visit`,
  type: FilterValueType.multiple,
  defaultOperator: `in`,
  customSearchParam: `visit`,
  options: [
    { id: `treatment`, name: `Treatment`, value: `treatment` },
    { id: `hygiene`, name: `Hygiene only`, value: `hygiene` },
    { id: `broken`, name: `Broken, not rebooked`, value: `broken` },
    { id: `none`, name: `Nothing booked`, value: `none` },
  ],
}

/** Whether a treatment patient has ever completed a visit, stored in `?seen=`. Read by `hiddenFromRules`. */
export const SEEN_BEFORE_RULE_COLUMN: Readonly<FilterableColumn> = {
  attribute: `seenBefore`,
  displayName: `Seen before`,
  type: FilterValueType.boolean,
  uniqOperator: `exactly`,
  defaultOperator: `exactly`,
  customSearchParam: `seen`,
  options: [
    { id: `yes`, name: `Yes`, value: `yes` },
    { id: `no`, name: `No`, value: `no` },
  ],
}
